import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { Dpad, RocketTakeoff, Magic, ExclamationTriangleFill, ArrowRight, X, Download } from 'react-bootstrap-icons';
import { PricingTier } from '../types/subscription.types';
import subscriptionService from '../services/subscriptionService';
import AlertBanner from '../components/AlertBanner';
import { useAuth } from '../contexts/AuthContext';
import { ENV } from '../utils/env';
import { getUtmParams } from '../utils/utmTracking';
import './SelectPlan.css';
import './PlanConfirmation.css';

interface UpgradePreview {
  currentPlan: string;
  newPlan: string;
  prorationAmount: number;
  totalAmount: number;
  currency: string;
  lineItems: Array<{
    description: string;
    amount: number;
  }>;
}

const PlanConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUserProfile, userProfile, currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [upgradePreview, setUpgradePreview] = useState<UpgradePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloadingData, setDownloadingData] = useState(false);

  // Get the selected plan from navigation state
  const selectedPlanName = location.state?.selectedPlan as string | undefined;

  const pricingTiers: PricingTier[] = [
    {
      name: 'starter',
      displayName: 'Starter',
      price: 0,
      priceId: '',
      interval: 'month',
      description: 'For exploring your capabilities',
      features: [
        '1 project total',
        '100 messages total',
        'Limited templates',
        '5 artifact generations total',
        '2 Figma exports total',
      ],
    },
    {
      name: 'pro',
      displayName: 'Early Adopter',
      price: 18.95,
      priceId: ENV.STRIPE_PRICE_ID_PRO || '',
      interval: 'month',
      recommended: true,
      description: 'For casual designers needing flexibility',
      features: [
        '10 projects total',
        '500 messages/month',
        'Unlimited templates',
        '50 artifact generations/month',
        '25 Figma exports/month',
        'Direct Figma plugin integration',
      ],
    },
    {
      name: 'ultra',
      displayName: 'Studio',
      price: 78.95,
      priceId: ENV.STRIPE_PRICE_ID_ULTRA || '',
      interval: 'month',
      description: 'For professionals and small teams',
      features: [
        '25 projects total',
        '1,000 messages/month',
        'Unlimited templates',
        'Unlimited artifact generations',
        'Unlimited Figma exports',
        'Early Beta feature access',
      ],
    },
  ];

  // Plan hierarchy for determining upgrades vs downgrades
  const planHierarchy: Record<string, number> = {
    starter: 0,
    pro: 1,
    ultra: 2,
  };

  const currentPlanName = userProfile?.subscription?.planName || 'starter';
  const currentTier = pricingTiers.find(t => t.name === currentPlanName);
  const selectedTier = pricingTiers.find(t => t.name === selectedPlanName);

  const isUpgrade = selectedPlanName
    ? (planHierarchy[selectedPlanName] || 0) > (planHierarchy[currentPlanName] || 0)
    : false;

  // Check if this is specifically an Ultra to Pro downgrade
  const isUltraToProDowngrade = currentPlanName === 'ultra' && selectedPlanName === 'pro';

  // Check if user has an active paid subscription
  const hasActiveSubscription =
    userProfile?.subscription?.planName &&
    userProfile.subscription.planName !== 'starter' &&
    userProfile.subscription.status === 'active';

  // Redirect if no plan selected
  useEffect(() => {
    if (!selectedPlanName) {
      navigate('/select-plan');
    }
  }, [selectedPlanName, navigate]);

  // Fetch upgrade preview if this is an upgrade
  useEffect(() => {
    const fetchUpgradePreview = async () => {
      if (!isUpgrade || !hasActiveSubscription || !selectedTier?.priceId) {
        return;
      }

      setLoadingPreview(true);
      try {
        const preview = await subscriptionService.previewUpgrade({
          priceId: selectedTier.priceId,
        });
        setUpgradePreview(preview);
      } catch (err: any) {
        console.error('Error fetching upgrade preview:', err);
        // Don't show error banner, just don't display proration info
      } finally {
        setLoadingPreview(false);
      }
    };

    fetchUpgradePreview();
  }, [isUpgrade, hasActiveSubscription, selectedTier?.priceId]);

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Stripe amounts are in cents
  };

  const getIcon = (tierName: string, size: number = 24) => {
    switch (tierName) {
      case 'starter':
        return <Dpad size={size} className="plan-icon" />;
      case 'pro':
        return <RocketTakeoff size={size} className="plan-icon" />;
      case 'ultra':
        return <Magic size={size} className="plan-icon" />;
      default:
        return <Dpad size={size} className="plan-icon" />;
    }
  };

  const formatBillingCycleEndDate = (periodEnd: Date | string | null | undefined | { _seconds?: number; seconds?: number }): string => {
    if (!periodEnd) return '';

    let endDate: Date;

    // Handle Firestore Timestamp objects
    if (typeof periodEnd === 'object' && periodEnd !== null && '_seconds' in periodEnd && periodEnd._seconds) {
      endDate = new Date(periodEnd._seconds * 1000);
    } else if (typeof periodEnd === 'object' && periodEnd !== null && 'seconds' in periodEnd && periodEnd.seconds) {
      endDate = new Date(periodEnd.seconds * 1000);
    } else {
      endDate = new Date(periodEnd as string | Date);
    }

    if (isNaN(endDate.getTime())) {
      return '';
    }

    return endDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleDownloadAccountData = async () => {
    setDownloadingData(true);
    setError(null);

    try {
      const token = await currentUser?.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/exports/account-data`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 404) {
          throw new Error('No data to export. Create some projects or conversations first.');
        }
        throw new Error(errorData.error || 'Failed to download account data');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      link.download =
        filenameMatch?.[1] || `revealer-data-${new Date().toISOString().split('T')[0]}.zip`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      setError(error.message || 'Failed to download account data. Please try again.');
      setShowErrorBanner(true);
    } finally {
      setDownloadingData(false);
    }
  };

  const handleKeepUltraPlan = () => {
    navigate('/dashboard');
  };

  const handleGoBack = () => {
    navigate('/select-plan');
  };

  const handleConfirm = async () => {
    if (!selectedTier) return;

    setLoading(true);
    setError(null);

    try {
      // Handle downgrade to starter (cancellation)
      if (selectedTier.name === 'starter') {
        await subscriptionService.cancelSubscription();
        await refreshUserProfile();
        setSuccessMessage('Your subscription has been canceled and will remain active until the end of your billing period.');
        setShowSuccessBanner(true);
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
        return;
      }

      // Check if user has an active paid subscription (upgrade/downgrade)
      const hasActiveSubscription =
        userProfile?.subscription?.planName &&
        userProfile.subscription.planName !== 'starter' &&
        userProfile.subscription.status === 'active';

      if (hasActiveSubscription) {
        // Update existing subscription
        const result = await subscriptionService.updateSubscription({
          priceId: selectedTier.priceId,
        });

        await refreshUserProfile();

        if (result.scheduledChange) {
          const effectiveDate = new Date(result.scheduledChange.effectiveDate).toLocaleDateString();
          setSuccessMessage(`Your plan will change to ${selectedTier.displayName} on ${effectiveDate} (end of your current billing period).`);
        } else {
          setSuccessMessage(`Your plan has been updated to ${selectedTier.displayName}.`);
        }
        setShowSuccessBanner(true);

        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        // New subscription - go through Stripe checkout
        const successUrl = `${window.location.origin}/select-plan?checkout=success`;
        const cancelUrl = `${window.location.origin}/select-plan?checkout=canceled`;

        const { url } = await subscriptionService.createCheckoutSession({
          priceId: selectedTier.priceId,
          successUrl,
          cancelUrl,
          utmParams: getUtmParams(),
        });

        window.location.href = url;
      }
    } catch (err: any) {
      console.error('Error processing plan change:', err);
      if (err.response?.status === 402) {
        setError('Payment failed. Please update your payment method and try again.');
      } else {
        setError(err.response?.data?.error || 'Failed to process plan change');
      }
      setShowErrorBanner(true);
      setLoading(false);
    }
  };

  if (!selectedTier || !currentTier) {
    return null;
  }

  const plansToShow = [currentTier, selectedTier];

  return (
    <>
      {showErrorBanner && error && (
        <AlertBanner
          message={error}
          type="error"
          onClose={() => setShowErrorBanner(false)}
          autoClose={true}
          autoCloseDelay={10000}
        />
      )}
      {showSuccessBanner && (
        <AlertBanner
          message={successMessage || "Plan updated successfully!"}
          type="success"
          onClose={() => {
            setShowSuccessBanner(false);
            setSuccessMessage(null);
          }}
          autoClose={false}
        />
      )}
      <div className="select-plan-page">
        <Container className="select-plan-container">
          <div className="select-plan-header">
            <h1>{isUltraToProDowngrade ? 'Confirm your plan update' : 'update your plan'}</h1>
            <p className="subtitle">{isUltraToProDowngrade ? 'REVIEW YOUR CHANGES' : 'REVIEW YOUR DETAILS'}</p>
          </div>

          {isUltraToProDowngrade ? (
            <>
              {/* Ultra to Pro Downgrade UI */}
              <div className="downgrade-comparison-container">
                <div className="downgrade-header-tab">
                  Features you'll lose or reduce
                </div>
                <div className="downgrade-comparison-card">
                  {/* Plan Headers */}
                  <div className="downgrade-plan-headers">
                    <div className="downgrade-feature-label"></div>
                    <div className="downgrade-plan-column ultra">
                      {getIcon('ultra', 28)}
                      <span className="downgrade-plan-name">Studio</span>
                    </div>
                    <div className="downgrade-plan-column pro">
                      {getIcon('pro', 28)}
                      <span className="downgrade-plan-name">Early Adopter</span>
                    </div>
                  </div>

                  {/* Comparison Rows */}
                  <div className="downgrade-comparison-row">
                    <div className="downgrade-feature-label">Projects</div>
                    <div className="downgrade-feature-value losing">
                      <X size={24} className="downgrade-icon-x" />
                      <span>25 projects</span>
                    </div>
                    <div className="downgrade-feature-value gaining">
                      <ArrowRight size={24} className="downgrade-icon-arrow" />
                      <span>10 projects</span>
                    </div>
                  </div>

                  <div className="downgrade-comparison-row">
                    <div className="downgrade-feature-label">Conversations</div>
                    <div className="downgrade-feature-value losing">
                      <X size={24} className="downgrade-icon-x" />
                      <span>1,000 messages/month</span>
                    </div>
                    <div className="downgrade-feature-value gaining">
                      <ArrowRight size={24} className="downgrade-icon-arrow" />
                      <span>500 messages/month</span>
                    </div>
                  </div>

                  <div className="downgrade-comparison-row">
                    <div className="downgrade-feature-label">Artifacts</div>
                    <div className="downgrade-feature-value losing">
                      <X size={24} className="downgrade-icon-x" />
                      <span>Unlimited artifact generations</span>
                    </div>
                    <div className="downgrade-feature-value gaining">
                      <ArrowRight size={24} className="downgrade-icon-arrow" />
                      <span>50 artifact generations/month</span>
                    </div>
                  </div>

                  <div className="downgrade-comparison-row">
                    <div className="downgrade-feature-label">Figma Exports</div>
                    <div className="downgrade-feature-value losing">
                      <X size={24} className="downgrade-icon-x" />
                      <span>Unlimited Figma exports</span>
                    </div>
                    <div className="downgrade-feature-value gaining">
                      <ArrowRight size={24} className="downgrade-icon-arrow" />
                      <span>25 Figma exports/month</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Warning Section */}
              <div className="downgrade-warning-container">
                <div className="downgrade-warning-card">
                  <h6 className="downgrade-warning-title">
                    <ExclamationTriangleFill size={18} className="downgrade-warning-icon" />
                    Attention: Data may be affected
                  </h6>
                  <p className="downgrade-warning-text">
                    By downgrading your account, you may need to delete projects and/or artifacts that are over your new capacity limits. You will have until the end of the billing cycle to download anything you'd like to save before they are automatically deleted, starting with the most recent.
                  </p>
                  <Button
                    variant="link"
                    className="downgrade-download-btn"
                    onClick={handleDownloadAccountData}
                    disabled={downloadingData}
                  >
                    {downloadingData ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Preparing...
                      </>
                    ) : (
                      <>
                        <Download size={14} className="me-1" />
                        DOWNLOAD ALL ACCOUNT DATA
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Billing Cycle Date */}
              <p className="downgrade-effective-date">
                Changes will take effect at the end of your current billing cycle: {formatBillingCycleEndDate(userProfile?.subscription?.currentPeriodEnd)}.
              </p>

              {/* Action Buttons */}
              <div className="downgrade-action-buttons">
                <Button
                  variant="outline-secondary"
                  className="downgrade-btn keep-btn"
                  onClick={handleKeepUltraPlan}
                  disabled={loading}
                >
                  KEEP STUDIO PLAN
                </Button>
                <Button
                  variant="primary"
                  className="downgrade-btn confirm-btn"
                  onClick={handleConfirm}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    'CONFIRM DOWNGRADE TO EARLY ADOPTER'
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Standard Confirmation UI */}
              <Row className="plans-row confirmation-plans-row">
                {plansToShow.map((tier) => (
                  <Col key={tier.name} className="mb-4 plan-col">
                    <div className="plan-card-wrapper">
                      {tier.name === currentPlanName && (
                        <div className="plan-label-above">Current Plan</div>
                      )}
                      {tier.name === selectedPlanName && (
                        <div className="plan-label-above">New Plan</div>
                      )}
                      <Card className={`plan-card ${tier.name === selectedPlanName ? 'highlighted' : ''}`}>
                        <Card.Body>
                          {getIcon(tier.name)}
                        <h2 className="plan-name">{tier.displayName}</h2>
                        <div className="plan-price">
                          {tier.name !== 'starter' && <span className="currency">$</span>}
                          <span className="price-amount">{tier.name === 'starter' ? 'Free' : tier.price}</span>
                          {tier.name !== 'starter' && <span className="price-period">/{tier.interval}</span>}
                          {tier.name === 'pro' && (
                            <span className="was-price"> was <s>$49</s></span>
                          )}
                          {tier.name === 'ultra' && (
                            <span className="was-price"> was <s>$149</s></span>
                          )}
                        </div>
                        {tier.description && (
                          <p className="plan-description">{tier.description}</p>
                        )}

                        <ul className="plan-features confirmation-features">
                          {tier.features.map((feature, idx) => (
                            <li key={idx}>{feature}</li>
                          ))}
                        </ul>
                      </Card.Body>
                    </Card>
                    </div>
                  </Col>
                ))}
              </Row>

              {/* Proration Info for Upgrades */}
              {isUpgrade && hasActiveSubscription && (
                <div className="proration-info">
                  {loadingPreview ? (
                    <div className="proration-loading">
                      <Spinner animation="border" size="sm" role="status">
                        <span className="visually-hidden">Loading billing details...</span>
                      </Spinner>
                      <span className="ms-2">Loading billing details...</span>
                    </div>
                  ) : upgradePreview ? (
                    <div className="proration-details">
                      <h6 className="proration-title">
                        <ExclamationTriangleFill size={16} color="#FD7E14" className="proration-icon" />
                        Billing Summary
                      </h6>
                      <p className="proration-note">
                        This amount will be charged to your payment method on file. Your new plan features will be available immediately.
                      </p>
                      <ul className="proration-line-items">
                        {upgradePreview.lineItems.map((item, idx) => (
                          <li key={idx} className="proration-line-item">
                            <span className="line-item-description">{item.description}</span>
                            <span className="line-item-amount">
                              {formatCurrency(item.amount, upgradePreview.currency)}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <div className="proration-total">
                        <span className="total-label">Amount due today</span>
                        <span className="total-amount">
                          {formatCurrency(upgradePreview.totalAmount, upgradePreview.currency)}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              <div className="plan-navigation-buttons">
                <Button
                  variant="outline-secondary"
                  className="plan-nav-btn back-btn"
                  onClick={handleGoBack}
                  disabled={loading}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  className="plan-nav-btn next-btn confirm"
                  onClick={handleConfirm}
                  disabled={loading}
                >
                  Confirm
                </Button>
              </div>
            </>
          )}

          {/* Activity Spinner Overlay */}
          {loading && (
            <div className="plan-loading-overlay">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Processing...</span>
              </Spinner>
            </div>
          )}
        </Container>
      </div>
    </>
  );
};

export default PlanConfirmation;
